<template>
  <div class="main">
    <div class="header with-action">
      <div class="title-group">
        <h1>Statistik-Dashboard</h1>
        <p v-if="selectedPool">{{ selectedPool.name }}</p>
      </div>
      <button @click="$router.push('/wrapped')" class="wrapped-btn frosted">
        <span class="icon">✨</span>
        <span class="text">Recap</span>
      </button>
    </div>

    <LoadingSpinner v-if="loading" message="Statistiken werden berechnet..." />

    <div v-if="!loading && statistics && statistics.cumulativeAlcohol.length > 0" class="dashboard-content">
      <!-- Gesamtkonsum über Zeit -->
      <div class="chart-container frosted">
        <h3>Alkohol-Verlauf (Gesamt)</h3>
        <apexchart
          type="area"
          height="300"
          width="100%"
          :options="areaChartOptions"
          :series="areaChartSeries"
        ></apexchart>
      </div>
      
      <!-- Vergleich: Top 5 Mitglieder Verlauf -->
      <div class="chart-container frosted">
        <h3>Top 5 Mitglieder (Verlauf)</h3>
        <apexchart
          type="area"
          height="350"
          width="100%"
          :options="multiAreaChartOptions"
          :series="multiAreaChartSeries"
        ></apexchart>
      </div>

      <!-- Getränke-Verteilung -->
      <div class="chart-container frosted">
        <h3>Getränke-Verteilung</h3>
        <apexchart
          type="donut"
          height="300"
          width="100%"
          :options="donutChartOptions"
          :series="donutChartSeries"
        ></apexchart>
      </div>

      <!-- Stunden-Aktivität -->
      <div class="chart-container frosted">
        <h3>Aktivität nach Uhrzeit</h3>
        <apexchart
          type="bar"
          height="250"
          width="100%"
          :options="barChartOptions"
          :series="barChartSeries"
        ></apexchart>
      </div>

      <!-- Benutzer-Vergleich: Alkohol -->
      <div class="chart-container frosted">
        <h3>Alkohol pro Mitglied (g)</h3>
        <apexchart
          type="bar"
          height="300"
          width="100%"
          :options="userAlcoholOptions"
          :series="userAlcoholSeries"
        ></apexchart>
      </div>

      <!-- Benutzer-Vergleich: Anzahl -->
      <div class="chart-container frosted">
        <h3>Drinks pro Mitglied</h3>
        <apexchart
          type="bar"
          height="300"
          width="100%"
          :options="userDrinksOptions"
          :series="userDrinksSeries"
        ></apexchart>
      </div>

      <!-- Mitglieder-Erfolge -->
      <div class="member-success-section" v-if="userBreakdown.length > 0">
        <h3>Mitglieder-Erfolge 🏆</h3>
        <div class="member-cards">
          <div class="member-card frosted" v-for="user in userBreakdown" :key="user.name">
            <div class="card-header">
              <h4>{{ user.name }}</h4>
              <span class="total-alc">{{ user.totalAlcohol }}g</span>
            </div>
            <div class="card-stats">
              <div class="stat-item">
                <p>Anzahl</p>
                <b>{{ user.count }}x</b>
              </div>
              <div class="stat-item">
                <p>Favorit</p>
                <b>{{ user.favoriteDrink }}</b>
              </div>
              <div class="stat-item">
                <p>Durchschnitt</p>
                <b>{{ user.avgVol }}% vol</b>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!loading && (!statistics || statistics.cumulativeAlcohol.length === 0)" class="center-msg">
      <h3>Noch keine Daten vorhanden</h3>
      <p>Trage zuerst ein paar Getränke ein!</p>
    </div>
  </div>
</template>

<script>
import AuthService from "@/services/AuthService.js";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import VueApexCharts from "vue3-apexcharts";
import moment from "moment";

export default {
  name: "DashboardView",
  components: {
    LoadingSpinner,
    apexchart: VueApexCharts,
  },
  data() {
    return {
      loading: true,
      statistics: null,
      areaChartSeries: [],
      areaChartOptions: {},
      donutChartSeries: [],
      donutChartOptions: {},
      barChartSeries: [],
      barChartOptions: {},
      userAlcoholSeries: [],
      userAlcoholOptions: {},
      userDrinksSeries: [],
      userDrinksOptions: {},
      multiAreaChartSeries: [],
      multiAreaChartOptions: {},
      userBreakdown: [],
    };
  },
  computed: {
    selectedPool() {
      return this.$store.getters.getSelectedPool;
    },
  },
  async mounted() {
    if (!this.selectedPool) {
      this.$router.push("/");
      return;
    }
    
    // Premium protection
    if (this.selectedPool.member_limit <= 5) {
      this.$router.push('/pool-settings?pool=' + (this.selectedPool.pool_id || this.selectedPool.id));
      return;
    }

    await this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const response = await AuthService.getPoolDashboardStatistics({
          pool_id: this.selectedPool.pool_id || this.selectedPool.id,
        });
        if (response.success) {
          this.statistics = response.msg;
          this.prepareChartData();
        }
      } catch (e) {
        console.error("Error fetching dashboard data:", e);
      } finally {
        this.loading = false;
      }
    },
    prepareChartData() {
      if (!this.statistics) return;

      const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';
      const textColor = isDarkMode ? '#FFFFFF' : '#000000';

      // 1. Area Chart (Cumulative Alcohol) - use pre-calculated data
      const areaData = this.statistics.cumulativeAlcohol.map(item => ({
        x: moment(item.timestamp).valueOf(),
        y: item.value
      }));

      this.areaChartSeries = [
        {
          name: "Gramm Alkohol (Gesamt)",
          data: areaData,
        },
      ];

      // Calculate dynamic min/max with a small buffer
      if (areaData.length > 0) {
        const timestamps = areaData.map(d => d.x);
        const minTs = Math.min(...timestamps);
        const maxTs = Math.max(...timestamps);
        
        // Buffer of 10% of the range, but at least 2 hours
        const range = maxTs - minTs;
        const buffer = Math.max(range * 0.1, 7200000); 

        this.areaChartOptions = {
          chart: {
            type: "area",
            toolbar: { show: false },
            zoom: { enabled: false },
            background: 'transparent',
            animations: { enabled: true }
          },
          colors: ["#52b69a"],
          stroke: { curve: "smooth", width: 4 },
          fill: {
            type: "gradient",
            gradient: {
              shadeIntensity: 1,
              opacityFrom: 0.45,
              opacityTo: 0.05,
            },
          },
          dataLabels: { enabled: false },
          xaxis: {
            type: "datetime",
            min: minTs - (buffer / 2),
            max: maxTs + (buffer / 2),
            labels: { 
              style: { colors: textColor },
              datetimeUTC: false,
              format: 'dd.MM.'
            },
            axisBorder: { show: false },
            axisTicks: { show: false }
          },
          grid: {
            borderColor: isDarkMode ? '#38383a' : '#E5E5EA',
            strokeDashArray: 4
          },
          yaxis: {
            labels: { style: { colors: textColor } },
          },
          tooltip: { 
            x: { format: "dd.MM. HH:mm" },
            theme: isDarkMode ? 'dark' : 'light' 
          },
          theme: { mode: isDarkMode ? 'dark' : 'light' }
        };
      }

      // 2. Donut Chart (Drink Distribution) - use pre-calculated data
      this.donutChartSeries = this.statistics.drinkDistribution.map(item => item.count);
      this.donutChartOptions = {
        labels: this.statistics.drinkDistribution.map(item => item.name),
        legend: {
          position: "bottom",
          labels: { colors: textColor },
        },
        stroke: { show: false },
        chart: { background: 'transparent' },
        theme: { mode: isDarkMode ? 'dark' : 'light' },
        colors: ["#184e77", "#d9ed92", "#99d98c", "#34a0a4", "#52b69a"],
        dataLabels: { enabled: false }
      };

      // 3. Bar Chart (Hourly Activity) - use pre-calculated data
      this.barChartSeries = [{ name: "Drinks", data: this.statistics.hourlyActivity }];
      this.barChartOptions = {
        chart: {
          type: "bar",
          toolbar: { show: false },
          background: 'transparent',
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: "60%",
          },
        },
        xaxis: {
          categories: Array.from({ length: 24 }, (_, i) => `${i}h`),
          labels: { 
            style: { colors: textColor },
            formatter: function(val) {
              const hour = parseInt(val);
              if (!isNaN(hour) && hour % 4 === 0) {
                return val;
              }
              return '';
            }
          },
        },
        yaxis: {
          labels: { style: { colors: textColor } },
        },
        colors: ["#52b69a"],
        dataLabels: { enabled: false },
        theme: { mode: isDarkMode ? 'dark' : 'light' }
      };

      // 4. User Alcohol Chart - use pre-calculated data
      this.userAlcoholSeries = [
        {
          name: "Alkohol (g)",
          data: this.statistics.userAlcohol.map(user => user.value),
        },
      ];
      this.userAlcoholOptions = {
        chart: { type: "bar", toolbar: { show: false }, background: 'transparent' },
        plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
        xaxis: {
          categories: this.statistics.userAlcohol.map(user => user.name),
          labels: { style: { colors: textColor } },
        },
        yaxis: { labels: { style: { colors: textColor } } },
        colors: ["#184e77"],
        dataLabels: { enabled: true, style: { colors: ['#fff'] } },
        theme: { mode: isDarkMode ? 'dark' : 'light' }
      };

      // 5. User Drinks Count Chart - use pre-calculated data
      this.userDrinksSeries = [
        {
          name: "Anzahl Drinks",
          data: this.statistics.userDrinks.map(user => user.value),
        },
      ];
      this.userDrinksOptions = {
        chart: { type: "bar", toolbar: { show: false }, background: 'transparent' },
        plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
        xaxis: {
          categories: this.statistics.userDrinks.map(user => user.name),
          labels: { style: { colors: textColor } },
        },
        yaxis: { labels: { style: { colors: textColor } } },
        colors: ["#34a0a4"],
        dataLabels: { enabled: true, style: { colors: ['#fff'] } },
        theme: { mode: isDarkMode ? 'dark' : 'light' }
      };

      // Find the global maximum timestamp and current total for each user's series
      let globalMaxTs = 0;
      if (areaData.length > 0) {
        globalMaxTs = Math.max(...areaData.map(d => d.x));
      }

      // 6. Multi-line Area Chart for Top Users
      this.multiAreaChartSeries = this.statistics.topUsers.map(user => {
        const userData = user.data.map(item => ({
          x: moment(item.timestamp).valueOf(),
          y: item.value
        }));

        // Extension: If the user's last entry is before the pool's overall last timestamp,
        // extend the line horizontally to that end timestamp.
        if (userData.length > 0 && globalMaxTs > 0) {
          const lastPoint = userData[userData.length - 1];
          if (lastPoint.x < globalMaxTs) {
            userData.push({
              x: globalMaxTs,
              y: lastPoint.y
            });
          }
        }

        return {
          name: user.name,
          data: userData
        };
      });

      this.multiAreaChartOptions = {
        ...this.areaChartOptions,
        colors: ["#184e77", "#d9ed92", "#99d98c", "#34a0a4", "#52b69a"],
        stroke: { curve: "smooth", width: 4 },
        legend: {
          show: true,
          position: 'bottom',
          labels: { colors: textColor }
        },
        fill: {
          type: 'solid',
          opacity: 0.1
        }
      };

      // 7. User Breakdown - use pre-calculated data
      this.userBreakdown = this.statistics.userBreakdown;
    },
  },
};
</script>

<style scoped>
.main {
  padding: 20px;
  padding-bottom: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.header.with-action {
  width: 100%;
  max-width: 600px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.title-group {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.wrapped-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  font-size: 0.95rem;
  font-weight: 700;
  border-radius: 100px;
  background: linear-gradient(135deg, #a41ead 0%, #ff5c8a 100%) !important;
  color: white !important;
  border: none !important;
  box-shadow: 0 4px 12px rgba(164, 30, 173, 0.3) !important;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.wrapped-btn:active {
  transform: scale(0.95);
}

.wrapped-btn .icon {
  font-size: 1.1rem;
}

h1 {
  margin: 0;
  font-size: 1.8rem;
}

.header p {
  margin: 5px 0 0 0;
  color: var(--tertiary-color);
  font-weight: 600;
}

.dashboard-content {
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
}

.chart-container {
  padding: 20px;
  width: 100%;
  box-sizing: border-box;
}

.chart-container h3 {
  margin: 0 0 15px 0;
  font-size: 1.1rem;
  color: var(--primary-color);
  opacity: 0.9;
}

.center-msg {
  margin-top: 50%;
  text-align: center;
}

[data-theme='dark'] .chart-container {
  background: rgba(255, 255, 255, 0.05) !important;
}

/* Member Success Section */
.member-success-section {
  width: 100%;
  max-width: 600px;
  margin-top: 20px;
}

.member-success-section h3 {
  margin-bottom: 20px;
  color: var(--primary-color);
  font-size: 1.3rem;
  text-align: left;
}

.member-cards {
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
}

.member-card {
  padding: 20px;
  border-radius: var(--radius-m);
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 10px;
}

.card-header h4 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--primary-color);
}

.total-alc {
  font-weight: 800;
  color: var(--primary-color);
  background: var(--background);
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 0.9rem;
}

.card-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.stat-item p {
  font-size: 0.75rem;
  margin: 0 0 4px 0;
  color: var(--tertiary-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-item b {
  font-size: 0.9rem;
  color: var(--primary-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

[data-theme='dark'] .member-card {
  background: rgba(255, 255, 255, 0.03) !important;
}
</style>
